import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Navbar = ({ logoUrl }) => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const isRTL = language === 'he';

  // Navigation links with dynamic translation
  const navLinks = [
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.whyUs'), href: '#why-us' },
    { label: t('nav.howItWorks'), href: '#how-it-works' },
    { label: t('nav.pricing'), href: '#pricing' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Detect active section
      const sections = navLinks.map(l => l.href.slice(1));
      for (const id of sections.reverse()) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 200) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'py-2' : 'py-4'
        }`}
        style={{
          background: isScrolled
            ? 'rgba(30, 64, 175, 0.6)'
            : 'rgba(30, 64, 175, 0.3)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderBottom: isScrolled ? '1px solid rgba(147, 197, 253, 0.2)' : 'none',
          boxShadow: isScrolled ? '0 4px 30px rgba(0, 0, 0, 0.3)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`flex items-center gap-3 cursor-pointer ${isRTL ? 'flex-row' : 'flex-row'}`}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <motion.img
              src={logoUrl}
              alt="ג'יני מובייל"
              className="w-10 h-10 rounded-full object-cover"
              style={{
                border: '2px solid rgba(147, 197, 253, 0.4)',
                boxShadow: '0 0 15px rgba(147, 197, 253, 0.2)',
              }}
              animate={isScrolled ? {} : { boxShadow: ['0 0 15px rgba(147, 197, 253, 0.2)', '0 0 25px rgba(147, 197, 253, 0.4)', '0 0 15px rgba(147, 197, 253, 0.2)'] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <div className={isRTL ? "text-right" : "text-left"}>
              <div className="font-bold text-white text-sm leading-tight">ג'יני מובייל</div>
              <div className="text-xs leading-tight" style={{ color: '#6ee7b7' }}>Genie Mobile</div>
            </div>
          </motion.div>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, i) => (
              <motion.button
                key={link.href}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i, duration: 0.5 }}
                onClick={() => scrollToSection(link.href)}
                className={`text-sm transition-colors duration-300 relative group ${
                  activeSection === link.href.slice(1) ? 'text-white' : 'text-blue-100 hover:text-white'
                }`}
              >
                {link.label}
                <motion.span
                  className="absolute -bottom-1 right-0 h-0.5 rounded-full"
                  style={{ background: 'linear-gradient(90deg, #0284c7, #60a5fa)' }}
                  initial={{ width: 0 }}
                  animate={{
                    width: activeSection === link.href.slice(1) ? '100%' : 0,
                  }}
                  transition={{ duration: 0.3 }}
                />
                <span
                  className="absolute -bottom-1 right-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 rounded-full"
                  style={{ background: 'linear-gradient(90deg, #0284c7, #60a5fa)' }}
                />
              </motion.button>
            ))}
          </div>

          {/* Language Toggle Button - Desktop */}
          <motion.button
            whileHover={{ scale: 1.05, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleLanguage}
            className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white"
            style={{
              background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.3), rgba(147, 197, 253, 0.4))',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 2px 10px rgba(96, 165, 250, 0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Globe size={18} />
            <span>{language === 'he' ? 'EN' : 'עב'}</span>
          </motion.button>

          {/* CTA Button - Desktop */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            whileHover={{ scale: 1.08, boxShadow: '0 8px 30px rgba(96, 165, 250, 0.4)' }}
            whileTap={{ scale: 0.92 }}
            className="hidden lg:flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold text-white magnetic-btn"
            style={{
              background: 'linear-gradient(135deg, #0284c7 0%, #60a5fa 50%, #0ea5e9 100%)',
              boxShadow: '0 4px 15px rgba(96, 165, 250, 0.3)',
            }}
            onClick={() => window.open('tel:0532222342')}
          >
            <Phone size={15} />
            {t('nav.callNow')}
          </motion.button>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, clipPath: 'circle(0% at 100% 0%)' }}
            animate={{ opacity: 1, clipPath: 'circle(150% at 100% 0%)' }}
            exit={{ opacity: 0, clipPath: 'circle(0% at 100% 0%)' }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mobile-menu fixed inset-0 z-40 pt-24 px-6"
            style={{ background: 'rgba(30, 64, 175, 0.6)', backdropFilter: 'blur(30px)' }}
          >
            <div className="flex flex-col items-center gap-6 mt-8">
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  onClick={() => scrollToSection(link.href)}
                  className="text-xl text-blue-100 hover:text-white transition-colors font-medium text-center w-full"
                >
                  {link.label}
                </motion.button>
              ))}
              
              {/* Language Toggle - Mobile */}
              <motion.button
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full text-base font-bold text-white"
                style={{
                  background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.3), rgba(147, 197, 253, 0.4))',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <Globe size={20} />
                <span>{language === 'he' ? 'English' : 'עברית'}</span>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-2 px-10 py-3.5 rounded-full text-base font-bold text-white mt-4"
                style={{ background: 'linear-gradient(135deg, #93c5fd, #6ee7b7, #5cc9bd)' }}
                onClick={() => window.open('tel:0532222342')}
              >
                <Phone size={18} />
                {t('nav.callNow')}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
